import React, { useState, useEffect, useContext } from 'react';
import { styled } from '@mui/material/styles';
import { PatientIDContext } from '../../Contexts';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';
import { IoBatteryFull, IoBatteryHalf, IoBatteryDead, IoBatteryCharging } from 'react-icons/io5';
import logo from '../logo.PNG';
import { getTrial } from '../../util/reportAPI';
import { listGopros, connectGopro, removeGopro, wakeGopro, sleepGopro } from '../../util/recordingAPI';
import TrialRow from '../patients/patientDashboard/TrialRow';
import Box from "@mui/material/Box";
import Button from '@mui/material/Button';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Alert } from '@mui/material';
import Fade from '@mui/material/Fade';
import '../../../dist/assets/stylesheets/css/device.css'
import { checkDevice, checkRpiConnection } from "../../util/recordingAPI";
import { rememberConnectedCameras } from '../../util/deviceConnectionMemory';

const CommonButton = styled(Button)({
  color: 'black',
  '& svg': {
    fontSize: '1.2rem',
    marginRight: '5px',
  }
});

function SetupPage() {
  const history = useHistory();
  const [submit, setSubmit] = useState(true);
  const [formDisabled, setFormDisabled] = useState(false);
  const [isScanning, setIsScanning] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [gopros, setGopros] = useState([]);
  const [goproStates, setGoproStates] = useState({}); // Store gopro_states from API response
  const [errorMsg, setErrorMsg] = useState(null);
  const [isConnecting, setIsConnecting] = useState({});
  const [anyConnecting, setAnyConnecting] = useState(false);
  const [isRemoving, setIsRemoving] = useState({});
  const [isWakingSleeping, setIsWakingSleeping] = useState({});
  const [openDialog, setOpenDialog] = React.useState(false);
  const [rpiConnected, setRpiConnected] = useState(null); // null = checking, true = connected, false = disconnected
  const [rpiChecking, setRpiChecking] = useState(false);
  const [query, setQuery] = useState('');
  const [checkResponse, setCheckResponse] = useState(null);
  const [cameraCheckMap, setCameraCheckMap] = useState({}); // Processed map: name -> check data

  useEffect(() => {
    handleScan()
  }, [])


  // Process check response into a lookup map and extract gopro_states
  useEffect(() => {
    if (!checkResponse) {
      setCameraCheckMap({});
      setGoproStates({});
      return;
    }

    console.log('=== PROCESSING CHECK RESPONSE ===', checkResponse);
    
    // Extract gopro_states from checkResponse if available
    if (checkResponse.gopro_states && typeof checkResponse.gopro_states === 'object') {
      console.log('=== EXTRACTING GOPRO_STATES FROM CHECK RESPONSE ===', checkResponse.gopro_states);
      console.log('=== ALL CAMERA KEYS IN GOPRO_STATES (from checkResponse) ===', Object.keys(checkResponse.gopro_states));
      console.log('=== NUMBER OF CAMERAS IN GOPRO_STATES (from checkResponse) ===', Object.keys(checkResponse.gopro_states).length);
      setGoproStates(checkResponse.gopro_states);
    }
    
    const map = {};
    
    // Structure 1: Object with cameras or result objects keyed by name
    if (checkResponse.cameras && typeof checkResponse.cameras === 'object') {
      Object.entries(checkResponse.cameras).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          const name = value.name || value.device || value.id || key;
          map[name] = value;
        }
      });
    }
    
    if (checkResponse.result && typeof checkResponse.result === 'object' && !Array.isArray(checkResponse.result)) {
      Object.entries(checkResponse.result).forEach(([key, value]) => {
        if (value && typeof value === 'object') {
          const name = value.name || value.device || value.id || key;
          map[name] = value;
        }
      });
    }
    
    // Structure 2: Array of camera objects
    const cameraArray = 
      Array.isArray(checkResponse.cameras) ? checkResponse.cameras :
      Array.isArray(checkResponse.result) ? checkResponse.result :
      Array.isArray(checkResponse) ? checkResponse : null;
    
    if (cameraArray) {
      cameraArray.forEach(cam => {
        if (cam && typeof cam === 'object') {
          const name = cam.name || cam.device || cam.id;
          if (name) {
            map[name] = cam;
          }
        }
      });
    }
    
    console.log('=== PROCESSED CAMERA CHECK MAP ===', map);
    setCameraCheckMap(map);
  }, [checkResponse])

  // Check RPi connection on mount and periodically
  useEffect(() => {
    checkRpiConnectionStatus();
    
    // Check connection every 10 seconds
    const interval = setInterval(() => {
      checkRpiConnectionStatus();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [])

  useEffect(() => {
    if (errorMsg) {
      setOpenDialog(true);
    }
  }, [errorMsg])


  const checkRpiConnectionStatus = () => {
    if (rpiChecking) return; // Prevent concurrent checks
    
    setRpiChecking(true);
    
    // Perform the check
    checkRpiConnection()
      .then(res => {
        const isConnected = res.connected === true;
        // Only update if the connection status actually changed
        setRpiConnected(prevConnected => {
          if (prevConnected !== isConnected) {
            return isConnected;
          }
          return prevConnected;
        });
      })
      .catch(err => {
        console.error("Error checking RPi connection:", err);
        // On error (network failure, timeout, etc.), mark as disconnected
        setRpiConnected(prevConnected => {
          if (prevConnected !== false) {
            return false;
          }
          return prevConnected;
        });
      })
      .finally(() => {
        setRpiChecking(false);
      });
  }


  const handleClick = () => {
    history.goBack();
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleScan = () => {
    setIsScanning(true);
    listGopros()
      .then(res => {
        if (res.status == 'success') {
          const goproList = Object.values(res.result);
          setGopros(goproList);
          // Remember connected count/names so Record page can detect drop-offs.
          rememberConnectedCameras(goproList);
          // Reset wake/sleep loading states on fresh scan to avoid stuck disabled buttons
          setIsWakingSleeping({});
        } else {
          setErrorMsg(res.reason || 'Unknown error during scan');
          setIsScanning(false);
        }
      })
      .catch(error => {
        setErrorMsg("Failed to connect to the hardware. Please ensure that you followed the instructions.");
        setIsScanning(false);
      })
      .finally(() => setIsScanning(false));
    
    // Refresh check data to get latest charging status and gopro_states
    checkDevice()
      .then(res => {
        console.log('=== CHECK DEVICE RESPONSE (on scan) ===', res);
        if (res && res.status === 'success') {
          setCheckResponse(res);
          
          // Extract gopro_states from checkDevice response
          if (res.gopro_states && typeof res.gopro_states === 'object') {
            console.log('=== GOPRO_STATES FROM CHECK DEVICE ===', res.gopro_states);
            console.log('=== ALL CAMERA KEYS IN GOPRO_STATES ===', Object.keys(res.gopro_states));
            console.log('=== NUMBER OF CAMERAS IN GOPRO_STATES ===', Object.keys(res.gopro_states).length);
            
            // Log each camera's state structure
            Object.entries(res.gopro_states).forEach(([cameraName, cameraState]) => {
              console.log(`=== STATE FOR ${cameraName} ===`, {
                'name': cameraState.name,
                'hasState': !!cameraState.state,
                'hasStatus': !!cameraState.state?.status,
                'status[2] (battery)': cameraState.state?.status?.[2],
                'status[54] (sd remaining)': cameraState.state?.status?.[54],
                'status[117] (sd total)': cameraState.state?.status?.[117]
              });
            });
            
            setGoproStates(res.gopro_states);
          } else {
            console.warn('gopro_states not found in checkDevice response');
            setGoproStates({});
          }
        } else {
          console.warn('Check device response status not success:', res);
          setGoproStates({});
        }
      })
      .catch(err => {
        console.error('Error checking device:', err);
        setGoproStates({});
      });
  };

  const handleUpdate = () => {
    setIsUpdating(true);
    fetch(`${window.UPDATE_URL}/api/update`)
      .then(response => response.json())
      .catch(error => {
        setErrorMsg('Error doing the update:', error);
        setIsUpdating(false)
      })
      .finally(() => setIsUpdating(false));
  };

  const connectGoproUI = (gopro) => {
    // Prevent connection if already connected
    if (gopro.connected) {
      return;
    }
    // Start connecting: set the specific gopro as connecting
    setIsConnecting(prevState => ({ ...prevState, [gopro.name]: true }));
    setAnyConnecting(true)

    connectGopro(gopro.name)
      .then(res => {
        // Handle non-success response
        if (res.status !== 'success') {
          setErrorMsg(res.reason || 'Unknown error during connection');
        }
        // Potentially update gopro list or status, assuming handleScan does this
        handleScan();
        setAnyConnecting(false)
      })
      .catch(err => {
        // Handle errors such as network issues
        setErrorMsg(err.message || 'Network error');
      })
      .finally(() => {
        // Stop connecting: set the specific gopro as not connecting
        // Ensures the UI reflects the latest state
        setIsConnecting(prevState => ({ ...prevState, [gopro.name]: false }));
        setAnyConnecting(false)
      });
  };


  const removeGoproUI = (gopro) => {
    setIsRemoving({ ...isRemoving, [gopro.name]: true });
    removeGopro(gopro.name)
      .then(res => {
        if (res.status !== 'success') {
          setErrorMsg(res.reason || 'Unknown error during connection');
          return;
        }
        // Optimistically remove from UI so row disappears and button state updates immediately
        setGopros(prev => {
          const next = prev.filter(g => g.name !== gopro.name);
          rememberConnectedCameras(next);
          return next;
        });
        setGoproStates(prev => {
          const next = { ...prev };
          delete next[gopro.name];
          return next;
        });
        handleScan();
      })
      .catch(err => {
        setErrorMsg(err.message || 'Network error');
      })
      .finally(() => {
        setIsRemoving({ ...isRemoving, [gopro.name]: false });
      });
  };

  const wakeOrSleepGoproUI = (gopro) => {
    const isConnected = gopro.connected === 'yes' || gopro.connected === true;
    const shouldSleep = isConnected;
    setIsWakingSleeping(prev => ({ ...prev, [gopro.name]: true }));
    const action = shouldSleep ? sleepGopro : wakeGopro;
    action(gopro.name)
      .then(res => {
        if (res && res.status !== 'success') {
          setErrorMsg(res.reason || `Unknown error during ${shouldSleep ? 'sleep' : 'wake'}`);
        }
        handleScan();
      })
      .catch(err => {
        setErrorMsg(err.message || 'Network error');
      })
      .finally(() => {
        setIsWakingSleeping(prev => ({ ...prev, [gopro.name]: false }));
      });
  };

  // Helper function to format SD card capacity in MB
  const formatSDCardCapacity = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      // If >= 1 GB, show in GB
      const gb = mb / 1024;
      return `${gb.toFixed(1)} GB`;
    } else {
      // Otherwise show in MB
      return `${mb.toFixed(0)} MB`;
    }
  };

  function GoProTable(data) {
    return (
      <section className='patient-table container'>
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '13%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan="8" style={{ height: "fit-content" }}>
                <input id='search-bar' placeholder="Search..." onChange={e => setQuery(e.target.value)}></input>
              </td>
            </tr>
            <tr>
              <th>Camera ID</th>
              <th>Availability</th>
              <th>Status</th>
              <th>Battery Level</th>
              <th>SD Card Capacity</th>
              <th>Connect</th>
              <th>Wake / Sleep</th>
              <th>Remove</th>
            </tr>
            {isScanning ? (
              // Show 4 skeleton rows while scanning
              Array.from({ length: 4 }).map((_, index) => (
                <tr key={`skeleton-${index}`} className="skeleton-row">
                  <td><div className="skeleton-cell" style={{ width: '80%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '60%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '70%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '50%' }}></div></td>
                  <td><div className="skeleton-cell" style={{ width: '50%' }}></div></td>
                  <td><div className="skeleton-button"></div></td>
                  <td><div className="skeleton-button"></div></td>
                  <td><div className="skeleton-button"></div></td>
                </tr>
              ))
            ) : gopros.length > 0 ? (
              gopros.map((gopro) => {
                const isAvailable = gopro.available === 'yes' || gopro.available === true;
                const isConnected = gopro.connected === 'yes' || gopro.connected === true;
                const isKnown = gopro.known === 'yes' || gopro.known === true;
                const canWake = isKnown && isAvailable && !isConnected;
                const canSleep = isKnown && isConnected;

                // Debug: Log camera name matching when there's a mismatch
                if (Object.keys(goproStates).length > 0 && !goproStates[gopro.name]) {
                  console.log(`=== CAMERA NAME MISMATCH FOR ${gopro.name} ===`, {
                    'Looking for': gopro.name,
                    'Available keys in goproStates': Object.keys(goproStates),
                    'Camera names from gopros array': gopros.map(g => g.name),
                    'Trying case-insensitive match...': true
                  });
                }
                
                // Try to find matching state data - check exact match first, then try case-insensitive
                let goproStateData = goproStates[gopro.name];
                if (!goproStateData) {
                  // Try case-insensitive match
                  const matchingKey = Object.keys(goproStates).find(key => 
                    key.toLowerCase() === gopro.name.toLowerCase()
                  );
                  if (matchingKey) {
                    goproStateData = goproStates[matchingKey];
                    console.log(`Found state data with case-insensitive match: ${gopro.name} -> ${matchingKey}`);
                  }
                }
                
                return (
                <tr key={gopro.name} style={{ backgroundColor: (isAvailable && isConnected) ? 'green' : 'inherit' }}>
                  <td>{gopro.name}</td>
                  <td>{isAvailable ? "yes" : "no"}</td>
                  <td style={{ color: isConnected ? 'green' : 'inherit' }}>
                    {isConnected ? "Connected" : "Not Connected"}
                  </td>
                  <td>
                    {(() => {
                      // Use the matched state data
                      if (!goproStateData) {
                        return '-';
                      }
                      
                      const state = goproStateData.state || {};
                      const status = state.status || {};
                      
                      // Use Status ID 2: Internal Battery Bars
                      // 0 = Zero, 1 = One, 2 = Two, 3 = Three, 4 = Charging
                      const batteryBars = status[2] ?? status['2'];
                      
                      // Debug: Log battery values for this specific camera
                      console.log(`=== BATTERY FOR ${gopro.name} ===`, {
                        'status[2] (battery bars)': batteryBars,
                        'status[70] (battery percent)': status[70] ?? status['70'],
                        'Display value': batteryBars === 4 ? 'Charging' : 
                                         batteryBars === 3 ? 'Full (3 bars)' :
                                         batteryBars === 2 ? 'Medium (2 bars)' :
                                         batteryBars === 1 ? 'Low (1 bar)' :
                                         batteryBars === 0 ? 'Empty (0 bars)' : 'Unknown',
                        'Has state data': !!goproStateData
                      });
                      
                      // Render appropriate visual based on battery bars value
                      // All icons use a lighter version of the UI color scheme (#5ab5bb - lighter version of #32858a)
                      if (batteryBars === 4) {
                        // Charging - use horizontal battery with plug icon
                        return <IoBatteryCharging style={{ fontSize: '28px', color: '#5ab5bb' }} title="Charging" />;
                      } else if (batteryBars === 3) {
                        // Three bars (full)
                        return <IoBatteryFull style={{ fontSize: '28px', color: '#5ab5bb' }} title="Full (3 bars)" />;
                      } else if (batteryBars === 2) {
                        // Two bars
                        return <IoBatteryHalf style={{ fontSize: '28px', color: '#5ab5bb' }} title="Medium (2 bars)" />;
                      } else if (batteryBars === 1) {
                        // One bar (low)
                        return <IoBatteryHalf style={{ fontSize: '28px', color: '#5ab5bb', opacity: 0.6 }} title="Low (1 bar)" />;
                      } else if (batteryBars === 0) {
                        // Zero bars (dead)
                        return <IoBatteryDead style={{ fontSize: '28px', color: '#5ab5bb' }} title="Empty (0 bars)" />;
                      }
                      
                      // Fallback to old battery percentage method if status[2] not available
                      const battery = 
                        status[70] ?? // State ID 70 = battery_percent (numeric key)
                        status['70'] ?? // State ID 70 = battery_percent (string key)
                        status.battery_percent ??
                        status.battery_level ?? 
                        status.battery ?? 
                        state.battery_percent ??
                        state.battery_level ?? 
                        state.battery ?? 
                        gopro.battery_level ?? 
                        gopro.battery;
                      
                      if (battery !== undefined && battery !== null) {
                        return `${battery}%`;
                      }
                      
                      return '-';
                    })()}
                  </td>
                  <td>
                    {(() => {
                      // Use the matched state data (same as battery level)
                      if (!goproStateData) {
                        return '-';
                      }
                      
                      const state = goproStateData.state || {};
                      const status = state.status || {};
                      
                      // Use State ID 54 for remaining and 117 for total SD card capacity
                      // State ID 54 = sd_card_remaining_pic (remaining capacity - value interpretation needed)
                      // State ID 117 = sd_card_total_pic (total capacity - value interpretation needed)
                      // Note: Values appear to need different conversion than bytes directly
                      const sdRemaining = status[54] ?? status['54'];
                      const sdTotal = status[117] ?? status['117'];
                      
                      // Helper function to convert KB to display value (number and unit)
                      // Values from status[54] and status[117] are in KILOBYTES (KB)
                      const convertKBToDisplay = (value) => {
                        if (value === undefined || value === null) return null;
                        
                        // Convert KB to GB: KB / (1024 * 1024)
                        const gb = value / (1024 * 1024);
                        
                        // If less than 1 GB, return in MB, otherwise return in GB
                        if (gb < 1) {
                          // Convert KB to MB: KB / 1024
                          const mb = value / 1024;
                          return { value: mb, unit: 'MB', displayGB: gb };
                        } else {
                          return { value: gb, unit: 'GB', displayGB: gb };
                        }
                      };
                      
                      // Display progress bar with "X free of Y" format
                      if (sdRemaining !== undefined && sdRemaining !== null && sdTotal !== undefined && sdTotal !== null) {
                        const remaining = convertKBToDisplay(sdRemaining);
                        const total = convertKBToDisplay(sdTotal);
                        
                        // Determine unit to use - use the larger unit (GB) if either value is >= 1 GB
                        const useGB = remaining.displayGB >= 1 || total.displayGB >= 1;
                        const unit = useGB ? 'GB' : 'MB';
                        
                        // Convert to the chosen unit for display
                        const remainingValue = useGB ? remaining.displayGB : (remaining.value || 0);
                        const totalValue = useGB ? total.displayGB : (total.value || 0);
                        
                        // Calculate used space (total - remaining)
                        const usedValue = totalValue - remainingValue;
                        const usedPercent = totalValue > 0 ? (usedValue / totalValue) * 100 : 0;
                        
                        // Format display values
                        const remainingFormatted = useGB 
                          ? remainingValue.toFixed(1) 
                          : Math.round(remainingValue);
                        const totalFormatted = useGB 
                          ? totalValue.toFixed(1) 
                          : Math.round(totalValue);
                        
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <div className="sd-card-progress-bar">
                              <div 
                                className="sd-card-progress-fill" 
                                style={{ width: `${Math.min(100, Math.max(0, usedPercent))}%` }}
                              ></div>
                            </div>
                            <div className="sd-card-progress-text">
                              {remainingFormatted} {unit} free of {totalFormatted} {unit}
                            </div>
                          </div>
                        );
                      }
                      
                      // Debug: Log if values are missing
                      console.log(`=== SD CARD MISSING FOR ${gopro.name} ===`, {
                        'status[54]': status[54] ?? status['54'],
                        'status[117]': status[117] ?? status['117'],
                        'Has state data': !!goproStateData,
                        'Status object exists': !!status,
                        'State object exists': !!state
                      });
                      
                      // If only one value is available, show it
                      if (sdRemaining !== undefined && sdRemaining !== null) {
                        return `${formatSDCardCapacity(sdRemaining)} remaining`;
                      }
                      
                      if (sdTotal !== undefined && sdTotal !== null) {
                        return `${formatSDCardCapacity(sdTotal)} total`;
                      }
                      
                      // Legacy fallback
                      const sdCapacity = 
                        status.sd_card_total_pic ??
                        status.sd_card_total_vid ??
                        status.sd_card_remaining_pic ??
                        status.sd_card_remaining_vid ??
                        status.sd_card_capacity ?? 
                        status.sd_capacity ?? 
                        status.sd_total_space ??
                        state.sd_card_total_pic ??
                        state.sd_card_total_vid ??
                        state.sd_card_capacity ?? 
                        state.sd_capacity ?? 
                        state.sd_total_space ??
                        gopro.sd_card_capacity ?? 
                        gopro.sd_capacity;
                      
                      if (sdCapacity !== undefined && sdCapacity !== null) {
                        return formatSDCardCapacity(sdCapacity);
                      }
                      
                      return '-';
                    })()}
                  </td>
                  <td>
                    <button
                      disabled={
                        anyConnecting ||
                        isConnected ||
                        !isAvailable || // if camera is not available, never allow Connect
                        (isKnown && isAvailable && !isConnected) // known + available + not connected -> use Wake instead of Connect
                      }
                      className='connect-button'
                      onClick={() => connectGoproUI(gopro)}>
                      {isConnecting[gopro.name] ? (
                        <span className='spinner'></span>
                      ) : (
                        "Connect"
                      )}
                    </button>
                  </td>
                  <td>
                    {!isKnown ? (
                      <span style={{ color: '#999' }}>—</span>
                    ) : (
                      <button
                        className={isConnected ? 'sleep-button' : 'wake-button'}
                        disabled={isWakingSleeping[gopro.name] || !(canWake || canSleep)}
                        onClick={() => wakeOrSleepGoproUI(gopro)}>
                        {isWakingSleeping[gopro.name] ? (
                          <span className='spinner'></span>
                        ) : (
                          isConnected ? 'Sleep' : 'Wake Up'
                        )}
                      </button>
                    )}
                  </td>
                  <td>
                    <button
                      className='remove-button'
                      onClick={() => removeGoproUI(gopro)}>
                      {(isRemoving[gopro.name] && !(gopro.available === 'yes' && gopro.connected === 'yes')) ? (
                        <span className='spinner'></span>
                      ) : (
                        "Remove"
                      )}
                    </button>
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: 'gray' }}>
                  No devices found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    );
  }


  const styles = {
    marginTop: '30px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
  };

  return (
    <div className='parent-container'>
      <div className='container'>
        <h1 className='page-title'>
          Cameras
        </h1>
      </div>

      {/* RPi Connection Status UI */}
      <div style={{ marginBottom: '10px', marginTop: '5px' }}>
        <Alert 
          severity={rpiConnected === true ? 'success' : rpiConnected === false ? 'warning' : 'info'}
        >
          <strong>RPi (Camera System) Status: </strong>
          {rpiConnected === true ? 'Connected' : 
           rpiConnected === false ? 'Disconnected' : 
           'Checking connection...'}
        </Alert>
      </div>

      <div style={styles}>
        <button disabled={isScanning} className='scan-button' onClick={handleScan}>{!isScanning ? 'Scan' : <span className='spinner'></span>}</button>
        <button type='submit' className='update-button' onClick={handleUpdate}>{!isUpdating ? 'Update Software' : <span className='spinner'></span>}</button>
      </div>
      <GoProTable data={gopros} />
      {/* pop up error */}
      <Box>
        <Dialog
          TransitionComponent={Fade}
          transitionDuration={{ enter: 260, exit: 180 }}
          open={openDialog}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Error"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {errorMsg}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <CommonButton onClick={handleDialogClose}>Close</CommonButton>
          </DialogActions>
        </Dialog>
      </Box>
      
      {/* {errorMsg &&
        <div className="error-modal">
          <div className="error-content">
            <h5>Error</h5>
            <p>{errorMsg}</p>
            <button className='close-button' onClick={() => setErrorMsg(null)}>Close</button>
          </div>
        </div>
      } */}
    </div>
  );
}

export default SetupPage;
